// 保護者の許可が要る操作を押したときの案内。閉じるだけの行き止まりにしない。
// - 未登録: 「みまもりせってい」（＝子ども向けに言いかえたアカウント登録）へ
// - 登録済: 「ほごしゃページへ」（許可を変えるのは保護者ページ）
// どちらも先におとなの確認を通す。

import { useState } from "react";
import { useSelector } from "react-redux";
import { useDialog } from "app/hook";
import { RootState } from "app/store";
import { DialogType } from "app/types";
import I18n from "common/i18n";
import { btnDelay, usePlaySound } from "common/utils";
import { AccountType } from "features/user/types";
import { ParentGate } from "features/parent/ParentGate";
import { InScreenModal } from "./ScreenModal";
import { BuilderModalPanel, BuilderModalAccent } from "./BuilderModalPanel";

export const BuilderAccountRequiredDialog = ({
  visible,
  title,
  message,
  close,
  accent = BuilderModalAccent.BLUE,
  icon = "lightbulb",
}: {
  visible: boolean;
  title: string;
  message: string;
  close: () => void;
  accent?: BuilderModalAccent;
  icon?: string;
}) => {
  const play = usePlaySound();
  const handleDialog = useDialog();
  const active = useSelector((state: RootState) => state.user.active);
  const isGuest = active?.account_type === AccountType.GUEST;
  const [gateVisible, setGateVisible] = useState(false);

  const handleClose = () => {
    play();
    btnDelay(() => close());
  };

  // 未登録は登録へ、登録済みは保護者ページへ。どちらもおとなの確認を先に通す
  const handleGoParent = () => {
    play();
    btnDelay(() => {
      if (isGuest) {
        setGateVisible(true);
        return;
      }
      // 登録済みは、おとなの確認・再認証・遷移を持つアプリ共通の導線に乗せる
      close();
      handleDialog({ type: DialogType.PARENT_GATE, value: true });
    });
  };

  // ゲストは保護者ページに入れないので、おとなの確認のあと保護者登録へ送る
  const handleGateConfirm = () => {
    setGateVisible(false);
    close();
    handleDialog({
      type: DialogType.PARENT_REGISTRATION,
      value: true,
      args: { redirectAfterSuccess: "/parent" },
    });
  };

  return (
    <>
      <InScreenModal open={visible} onBackdropClose={close}>
        <BuilderModalPanel accent={accent} icon={icon} title={title} width={480}>
          <div className="flex-col-el items-start w-full">
            {message?.split("\n").map((line, index) => (
              <p key={index} className="type-text-left type-body text-textcolor-black mb-2">
                {line}
              </p>
            ))}
          </div>

          <div className="flex-row-el flex-center w-full gap-2 px-4 upsilon">
            <button type="button" onClick={handleClose} className="type-inherit-font btn btn-blue">
              <div className="flex-row-center gap-1">
                <span className="material-symbols-outlined text-white type-md">close</span>
                <p className="type-text type-body text-white">{I18n.t("MSG_CLOSE_BTN")}</p>
              </div>
            </button>
            <button
              type="button"
              onClick={handleGoParent}
              className="type-inherit-font btn btn-primary"
            >
              <div className="flex-row-center gap-1">
                <span className="material-symbols-outlined text-white type-md">
                  {isGuest ? "notification_important" : "admin_panel_settings"}
                </span>
                <p className="type-text type-body text-white whitespace-nowrap">
                  {I18n.t(
                    isGuest
                      ? "MSG_PARENT_REGISTRATION_PROMPT_CTA"
                      : "MSG_UPGRADE_PROMOTION_UPGRADE_BTN",
                  )}
                </p>
              </div>
            </button>
          </div>
        </BuilderModalPanel>
      </InScreenModal>

      <ParentGate
        visible={gateVisible}
        confirm={handleGateConfirm}
        close={() => setGateVisible(false)}
      />
    </>
  );
};
